import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Search, MapPin, Loader2, BadgeCheck, Hammer, Scissors, HandMetal, Eye, Palette, Heart,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatGBP } from '@/lib/utils';

const CATEGORIES = [
  { key: 'all', label: 'All', icon: Search },
  { key: 'Barber', label: 'Barber', icon: Scissors },
  { key: 'Nails', label: 'Nails', icon: HandMetal },
  { key: 'Beauty', label: 'Beauty', icon: Eye },
  { key: 'Makeup', label: 'Makeup', icon: Palette },
  { key: 'Massage', label: 'Massage', icon: Heart },
];

function PartnerCard({ partner, onView, onBook }) {
  return (
    <Card className="p-4 border-slate-200" data-testid={`partner-card-${partner.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <h3 className="text-sm font-semibold text-slate-900 truncate">{partner.business_name}</h3>
            {partner.verified_badge && (
              <BadgeCheck className="h-4 w-4 text-emerald-600 shrink-0" aria-label="Verified" />
            )}
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5">
            {partner.category} · <MapPin className="h-3 w-3 inline" /> {partner.base_postcode}
          </p>
        </div>
        {partner.starting_price != null && (
          <div className="text-right shrink-0">
            <p className="text-[10px] uppercase tracking-wider text-slate-400">From</p>
            <p className="text-sm font-bold text-slate-900">{formatGBP(partner.starting_price)}</p>
          </div>
        )}
      </div>
      {partner.bio && (
        <p className="mt-2 text-xs text-slate-600 line-clamp-2">{partner.bio}</p>
      )}
      <div className="mt-2 flex flex-wrap gap-1">
        {partner.category === 'Barber' && partner.cleanup_included && (
          <span className="inline-flex items-center gap-1 text-[10px] rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5">
            <Hammer className="h-2.5 w-2.5" /> Cleanup included
          </span>
        )}
        {partner.verified_badge && (
          <span className="inline-flex items-center gap-1 text-[10px] rounded-full bg-rose-50 text-rose-700 border border-rose-200 px-2 py-0.5">
            <BadgeCheck className="h-2.5 w-2.5" /> Verified
          </span>
        )}
      </div>
      <div className="mt-3 flex gap-2">
        <Button size="sm" variant="outline" className="flex-1" onClick={() => onView(partner.id)} data-testid={`btn-view-${partner.id}`}>
          View profile
        </Button>
        <Button size="sm" className="flex-1 bg-rose-600 hover:bg-rose-700 text-white" onClick={() => onBook(partner.id)} data-testid={`btn-book-${partner.id}`}>
          Book
        </Button>
      </div>
    </Card>
  );
}

export default function CustomerHome() {
  const navigate = useNavigate();
  const [category, setCategory] = useState('all');
  const [postcode, setPostcode] = useState('HA1');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const search = useCallback(async () => {
    setLoading(true);
    setHasSearched(true);
    try {
      const params = {};
      if (category !== 'all') params.category = category;
      if (postcode) params.postcode = postcode.toUpperCase();
      const { data } = await api.get('/customer/search-partners', { params });
      setResults(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Search failed');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [category, postcode]);

  // Auto-search on mount with defaults
  useEffect(() => {
    search();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-slate-900">Find pros near you</h2>
        <p className="text-xs text-slate-500">Verified beauty &amp; grooming professionals in Harrow.</p>
      </div>

      <Card className="p-3 border-slate-200 space-y-2">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <MapPin className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <Input
              data-testid="search-postcode"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              className="h-11 pl-9 font-mono"
              placeholder="Postcode (e.g. HA1)"
            />
          </div>
          <Button onClick={search} disabled={loading} className="h-11 px-5 bg-slate-900 hover:bg-slate-800 text-white" data-testid="btn-search">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          </Button>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              onClick={() => setCategory(c.key)}
              data-testid={`cat-${c.key}`}
              className={`shrink-0 flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${
                category === c.key
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
              }`}
            >
              <c.icon className="h-3 w-3" /> {c.label}
            </button>
          ))}
        </div>
      </Card>

      <div className="space-y-3">
        {loading && (
          <p className="text-center text-sm text-slate-500 py-6">
            <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Searching nearby pros...
          </p>
        )}
        {!loading && hasSearched && results.length === 0 && (
          <Card className="p-6 text-center border-slate-200">
            <Search className="h-8 w-8 text-slate-300 mx-auto" />
            <p className="mt-2 text-sm text-slate-600">No verified partners found within 5 km.</p>
            <p className="text-xs text-slate-400 mt-1">Try a different postcode or category.</p>
          </Card>
        )}
        {!loading && results.map((p) => (
          <PartnerCard
            key={p.id}
            partner={p}
            onView={(id) => navigate(`/customer/partner/${id}`)}
            onBook={(id) => navigate(`/customer/partner/${id}`)}
          />
        ))}
      </div>
    </div>
  );
}
